import { httpClient, ApiResponse } from './http.client';

export interface SubscriptionPlan {
  plan_id: string;
  tier: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  display_name: string;
  description: string;
  price_monthly: number;
  price_annual: number;
  currency: string;
  max_users: number;
  max_projects: number;
  storage_limit_mb: number;
  features: string[];
  paystack_plan_id_monthly: string | null;
  paystack_plan_id_annual: string | null;
  paypal_plan_id_monthly: string | null;
  paypal_plan_id_annual: string | null;
}

export interface ActiveGateway {
  name: string;
  display_name: string;
  has_public_key: boolean;
}

export interface BillingStatus {
  status: 'trialing' | 'active' | 'expired' | 'past_due';
  is_trial: boolean;
  trial_days_remaining: number | null;
  trial_ends_at?: string;
  subscription_expires_at: string | null;
  plan: SubscriptionPlan | null;
  subscription_id: string | null;
}

export interface CheckoutSession {
  authorization_url: string;
  access_code?: string;
  reference: string;
  gateway?: string;
}

class BillingServiceClass {
  async getBillingStatus(): Promise<ApiResponse<BillingStatus>> {
    return httpClient.get<BillingStatus>('/api/v1/billing/status');
  }

  async getPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    return httpClient.get<SubscriptionPlan[]>('/api/v1/billing/plans');
  }

  async getActiveGateways(): Promise<ApiResponse<ActiveGateway[]>> {
    return httpClient.get<ActiveGateway[]>('/api/v1/billing/gateways');
  }

  async initiateCheckout(params: {
    plan_id: string;
    billing_period: 'monthly' | 'annual';
    gateway: 'paystack' | 'paypal';
    payer_email: string;
    payer_phone?: string;
    callback_url?: string;
  }): Promise<ApiResponse<CheckoutSession>> {
    return httpClient.post<CheckoutSession>('/api/v1/billing/checkout/initiate', params);
  }

  async verifyPayment(reference: string): Promise<ApiResponse<any>> {
    return httpClient.post<any>('/api/v1/billing/checkout/verify', { reference });
  }

  async verifyPaypalPayment(subscription_id: string): Promise<ApiResponse<any>> {
    return httpClient.post<any>('/api/v1/billing/checkout/verify/paypal', { subscription_id });
  }

  async startTrial(): Promise<ApiResponse<BillingStatus>> {
    return httpClient.post<BillingStatus>('/api/v1/billing/trial/start');
  }
}

export const billingService = new BillingServiceClass();
